import React from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import ProfileCard from '@atlassian/jira-profilecard-next/src/ui/profilecard/sync-experiment.tsx';
import type { CmdbObjectAttributeUser } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';

export type Props = {
	attribute: CmdbObjectAttributeUser;
};

export const UserAttribute = (props: Props): React.JSX.Element => {
	const { attribute } = props;

	return (
		<Wrapper>
			{attribute.objectAttributeValues.map((value, index) => {
				if (!value?.user) {
					/* Given we're in the user attribute the data structure expects user to be here,
					 * but we're seeing instances where it's not. Logging here to keep track of it happening.
					 * This can be removed if logs show this isn't happening anymore.
					 * More Info: https://hello.jira.atlassian.cloud/browse/FLY-1871
					 * */
					log.safeErrorWithoutCustomerData(
						'servicedesk.insight-common.insight-attribute-list.ui.user-attribute',
						`missing-user-attribute: ${attribute.objectAttributeValues.length}, valuePresent: ${!!value}`,
					);
					return;
				}

				return (
					value.user.key && (
						<ProfileCard key={index} accountId={value.user.key}>
							<UserWrapper>
								<Avatar size="small" src={value.user.avatarUrl} />
								<UserName>{value.user.displayName}</UserName>
							</UserWrapper>
						</ProfileCard>
					)
				);
			})}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	marginLeft: token('space.negative.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.050'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.100'),

	color: token('color.text'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	cursor: 'default',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserName = styled.span({
	marginLeft: token('space.050'),
});
